import styled, { css } from 'styled-components';

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 20px;

  h4 {
    font-size: 17px;
    font-weight: 700;
    color: ${props => props.theme.main.color};
    margin: 0 0 4px;
  }

  p {
    font-size: 13px;
    color: #5f6368;
    margin: 0;
    line-height: 1.45;
  }
`;

export const HeaderIcon = styled.div`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff1ea;
`;

export const LinkCard = styled.div<{ highlight?: boolean }>`
  border: 1px solid #e3e3e8;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fafafc;

  ${props =>
    props.highlight &&
    css`
      border-color: #21ba45;
      background: #f3fbf5;
    `}
`;

export const LinkLabel = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8a8f98;
  margin-bottom: 8px;
`;

export const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const LinkValue = styled.a`
  flex: 1 1 220px;
  min-width: 0;
  font-size: 13px;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  color: #1f2933;
  background: #fff;
  border: 1px solid #e3e3e8;
  border-radius: 6px;
  padding: 8px 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: #2185d0;
  }
`;

export const MetaList = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;

  li {
    font-size: 13px;
    color: #4b5563;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  i.icon {
    color: #9ca3af;
    margin: 0;
  }
`;

export const Section = styled.div`
  margin-top: 20px;
`;

export const Timeline = styled.ol`
  list-style: none;
  margin: 8px 0 0;
  padding: 0 0 0 4px;
`;

const processingColors: Record<string, string> = {
  Sucesso: '#21ba45',
  Erro: '#db2828',
  Alerta: '#fbbd08'
};

export const TimelineItem = styled.li<{ processing?: string | null }>`
  position: relative;
  padding: 0 0 14px 22px;
  border-left: 2px solid #ececf1;
  margin-left: 5px;

  &:last-child {
    border-left-color: transparent;
    padding-bottom: 0;
  }

  &::before {
    content: '';
    position: absolute;
    left: -7px;
    top: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px #d4d4dc;
    background: ${props => processingColors[props.processing || ''] || '#b0b4bc'};
  }

  strong {
    display: block;
    font-size: 13px;
    color: #1f2933;
  }

  span {
    display: block;
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }
`;

export const ConfirmBox = styled.div`
  text-align: center;
  padding: 4px 8px 0;

  h4 {
    font-size: 18px;
    font-weight: 700;
    margin: 12px 0 8px;
    color: #1f2933;
  }

  p {
    font-size: 14px;
    color: #4b5563;
    margin: 0 auto;
    max-width: 440px;
    line-height: 1.5;
  }
`;

export const StatusCircle = styled.div<{ tone: 'warning' | 'success' }>`
  width: 56px;
  height: 56px;
  margin: 0 auto;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => (props.tone === 'success' ? '#e7f7ec' : '#fff6e0')};

  i.icon {
    margin: 0;
    color: ${props => (props.tone === 'success' ? '#21ba45' : '#f2a900')};
  }
`;

export const InfoList = styled.ul`
  list-style: none;
  margin: 20px 0 0;
  padding: 14px 16px;
  border-radius: 10px;
  background: #f6f7fb;
  text-align: left;

  li {
    display: flex;
    gap: 8px;
    font-size: 13px;
    color: #374151;
    line-height: 1.45;
  }

  li + li {
    margin-top: 8px;
  }

  i.icon {
    color: #2185d0;
    margin: 1px 0 0;
  }
`;

export const ChannelPicker = styled.div`
  margin-top: 20px;
  text-align: left;

  label {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: #1f2933;
    margin-bottom: 8px;
  }
`;

export const ChannelOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
`;

export const ChannelOption = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${props => (props.active ? '#2185d0' : '#d4d4dc')};
  background: ${props => (props.active ? '#eef6fd' : '#fff')};
  color: ${props => (props.active ? '#2185d0' : '#4b5563')};
  transition: all 0.15s ease;

  &:focus-visible {
    outline: 2px solid #2185d0;
    outline-offset: 2px;
  }

  i.icon {
    margin: 0;
  }
`;

export const FieldHint = styled.span<{ error?: boolean }>`
  display: block;
  font-size: 12px;
  margin-top: 6px;
  color: ${props => (props.error ? '#db2828' : '#6b7280')};
`;

export const SendingBox = styled.div`
  text-align: center;
  padding: 28px 8px 12px;

  h4 {
    font-size: 16px;
    font-weight: 700;
    margin: 18px 0 6px;
    color: #1f2933;
  }

  p {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }
`;

export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;

  .ui.button {
    margin: 0;
  }

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    align-items: stretch;

    .ui.button {
      width: 100%;
    }
  }
`;
